<template>
  <Toast position="center"/>
  <div v-if="loading" class="bg-gray-50">
    <h2>Authenticating...</h2>
  </div>
  <div v-else class="vh-100 bg-gray-50">
    <!-- Header Bar -->
    <div class="flex pt-4 app-shell-top-bar sm:px-4 drop-shadow-lg">
      <div class="flex-none w-[205px] logo-container sticky top-0">
        <div class="float-left">
          <RmsLogo :logoColor="colorMode" />
        </div>
        <!-- Non-Production Info Message -->
        <div v-if="AppConfigStore.methods.getEnv() !== 'production'" class="float-right bg-[#B91C1C] p-2 h-full">
          <p class="text-center font-bold uppercase">not</p>
          <p class="text-center font-bold uppercase">production</p>
          <p class="text-center text-xs uppercase">{{ AppConfigStore.methods.getEnv() }}</p>
        </div>
      </div>
      <div class="flex-1">
        <form class="hidden" action="#" method="GET">
          <label for="search-field" class="sr-only">Search all files</label>
          <div class="relative w-full text-gray-500 focus-within:text-gray-600">
            <div class="absolute inset-y-0 pt-2 pointer-events-none left-5">
              <SearchIcon class="w-6 h-6 align-baseline" aria-hidden="true" />
            </div>
            <input
              id="search-field"
              name="search-field"
              class="w-full h-full py-2 pr-3 text-base text-gray-900 placeholder-gray-500 border-transparent rounded-md pl-14 focus:outline-none focus:ring-0 focus:border-transparent focus:placeholder-gray-400"
              placeholder="Search"
              type="search"
            />
          </div>
        </form>
      </div>

      <div class="ml-4 text-s">
        <Button
          type="button"
          icon="pi pi-user"
          class="user-profile-button"
          :label="UserDetailsStore.methods.getName()"
          aria:haspopup="true"
          aria-controls="overlay_panel"
          @click="toggle"
        />

        <OverlayPanel
          id="overlay_panel"
          ref="op"
          appendTo="body"
          :showCloseIcon="false"
          style="width: 175px"
          :breakpoints="{'960px': '75vw'}"
        >
          <ul class="w-full">
            <li class="w-full">
              <a
                class="inline-block w-full px-4 py-2 text-lg transition-all duration-200 ease-in-out hover:bg-blue-100"
                href="/logout"
              >Logout</a>
            </li>
          </ul>
        </OverlayPanel>
      </div>
    </div>

    <div class="sticky top-0 left-0 flex h-screen">
      <!-- Sidebar -->
      <div
        :class="largeNavOpen ? 'relative left-0 w-[220px] pb-10 overflow-auto' : 'h-full absolute -left-[220px] w-[220px]'"
        class="z-50 transition-all duration-500 ease-in-out sidebar-container"
      >
        <div class="w-full py-4">
          <div class="w-full pt-4 pb-4">
            <SideBarNav
              :navToggle="toggleMenuSize"
              :navItems="navigationStore.methods.getItems()"
              @onClick="onNavClick"
            ></SideBarNav>
          </div>
        </div>
      </div>
      <div
        :class="!largeNavOpen ? 'relative left-0 w-[40px] overflow-auto pb-10' : 'h-full absolute  -left-[100px]'"
        class="z-50 transition-all duration-500 ease-in-out sidebar-container"
      >
        <div class="w-full py-4">
          <div class="w-full pb-4 text-center">
            <SideBarNavSmall
              :navToggle="toggleMenuSize"
              :navItems="navigationStore.methods.getItems()"
              @onClick="toggleMenuSize"
            ></SideBarNavSmall>
          </div>
        </div>
      </div>

      <Transition>
        <div v-show="largeNavOpen" class="absolute version-container w-[185px] bottom-20 left-4 z-50">
          <p class="text-sm text-gray-300">RMS v{{ AppConfigStore.methods.getVersion() }}</p>
        </div>
      </Transition>

      <Transition>
        <div v-show="!largeNavOpen" class="absolute version-container bottom-20 left-2 z-50">
          <p class="text-sm text-gray-300">RMS</p>
          <p class="text-xs text-gray-300">v{{ AppConfigStore.methods.getVersion() }}</p>
        </div>
      </Transition>

      <!-- Content area -->
      <div class="flex-row flex-1 overflow-auto border-2">
        <!-- Main content -->
        <div class="flex overflow-auto primary-content main-content-container">
          <main class="flex-auto">
            <!-- Primary column -->
            <section aria-labelledby="primary-heading" class="h-full">
              <!-- primary content from Vue router loaded here -->
              <div class="w-full px-5 py-1">
                <router-view />
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, provide, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { SearchIcon } from '@heroicons/vue/solid'
import Button from 'primevue/button'
import OverlayPanel from 'primevue/overlaypanel'
import RmsLogo from '@/components/design/RmsLogo.vue'
import SideBarNav from '@/components/navigation/SideBarNav.vue'
import SideBarNavSmall from '@/components/navigation/SideBarNavSmall.vue'
import UsersService from '@/services/accounts/UsersService'
import AccountsService from '@/services/accounts/AccountsService'
import AppService from '@/services/AppService'
import UserDetailsStore from '@/stores/UserDetailsStore'
import NavigationStore from '@/stores/NavigationStore'
import AppConfigStore from '@/stores/AppConfigStore'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

export default {
  components: {
    SideBarNav,
    SideBarNavSmall,
    SearchIcon,
    Button,
    OverlayPanel,
    Toast,
    RmsLogo,
  },
  setup() {
    provide('UserDetailsStore', UserDetailsStore)
    const largeNavOpen = ref(true)
    const colorMode = ref('light_sans_company')
    const loading = ref(true)
    const userService = new UsersService()
    const appService = new AppService()
    const accountService = new AccountsService()
    const navigationStore = NavigationStore
    const op = ref()
    const toast = useToast()

    const toggle = (event) => op.value.toggle(event)
    const onNavClick = (itemMap) => navigationStore.methods.changeCurrentNav(itemMap)
    const toggleMenuSize = () => largeNavOpen.value = !largeNavOpen.value
    const currentRoute = computed(() => useRoute().name)

    const loadData = () => {
      Promise.all([
        appService.config(),
        userService.user(),
        userService.roles(),
      ])
        .then((responses) => {
          handleAppResponse(responses[0])
          const userAccountId = handleUserResponse(responses[1])
          handleRolesResponse(responses[2])
          if (!userAccountId) {
            handleUserAccountNotFound()
            throw new Error('User account not found')
          }
          return userAccountId
        })
        .then((userAccountId) => {
          return new Promise((resolve, reject) => {
            accountService.retrieve(userAccountId).then(handleAccountResponse)
              .then(() => resolve(true))
              .catch(err => reject(err))
          })
        })
        .then(() => {
            navigationStore.methods.init(UserDetailsStore)
            loading.value = false
        })
        .catch(err => console.log(err))
    }

    const handleAppResponse = (response) => {
      AppConfigStore.methods.setEnv(response.env)
      AppConfigStore.methods.setTimezone(response.timezone)
      AppConfigStore.methods.setURL(response.url)
      AppConfigStore.methods.setName(response.name)
      AppConfigStore.methods.setLocale(response.locale)
      AppConfigStore.methods.setMaintenance(response.maintenance)
      AppConfigStore.methods.setVersion(response.version)
    }

    const handleUserResponse = (response) => {
      UserDetailsStore.methods.setId(response.id)
      UserDetailsStore.methods.setName(response.name)
      return response.account_id
    }

    const handleAccountResponse = (account) => {
      UserDetailsStore.methods.setAccount({
        id: account.id,
        code: account.code,
      })
    }

    const handleRolesResponse = (response) => {
      UserDetailsStore.methods.setRoles(response.map(role => ({
        id: role.id,
        name: role.name,
        oktaId: role.okta_id,
      })))
    }

    const handleUserAccountNotFound = () => {
      toast.add({
        severity: 'error',
        summary: 'User account not found',
        detail: 'User account not found. Please contact an administrator.',
        life: undefined,
      })
    }

    onMounted(() => {
      loadData()
    })

    return {
      colorMode,
      loading,
      largeNavOpen,
      navigationStore,
      currentRoute,
      SideBarNav,
      SideBarNavSmall,
      onNavClick,
      AppConfigStore,
      UserDetailsStore,
      op,
      toggle,
      toggleMenuSize,
    }
  },
}
</script>

<style lang="scss">
  .header-space {
    height: calc(100vh - 76px);
  }
  .main-content-container {
    height: calc(100vh - 70px)
  }
  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
</style>
